import React, { useEffect, useState, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    TextField,
    Autocomplete,
    MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import { addTeam, getTeams } from "../../../components/teams/teamsSlice";
import { getSettings } from "../../settings/settingsSlice";
import { getClients } from "../../ventr/VentrSlice";
import { NumberInput } from "../../../components/inputs/NumberInput";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(0,0,0,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(0,0,0,0.18)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const LOCATIONS = [
    "INSITU",
    "MERIGNAC",
    "LGC",
    "GRADIGNAN",
    "NEUILLY",
    "INCONNU",
    "BEYROUTH",
    "ILE MAURICE",
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileAddTeamModal({ visible, setVisible }) {
    const dispatch = useDispatch();

    const clients = useSelector((state) => state.clients.list);

    const [name, setName] = useState("");
    const [sousService, setSousService] = useState("");
    const [localization, setLocalization] = useState("");
    const [lunchBreakDuration, setLunchBreakDuration] = useState(0);
    const [fraisTransport, setFraisTransport] = useState(0);
    const [forfaitDeplacement, setForfaitDeplacement] = useState(0);
    const [attempt, setAttempt] = useState(false);
    const [client, setClient] = useState(null);
    const [clientOptions, setClientOptions] = useState([]);

    /* ---- Side-effects ---- */
    useEffect(() => {
        if (visible) {
            dispatch(getSettings());
            dispatch(getClients());
        }
    }, [visible]);

    useEffect(() => {
        if (clients && clients.length > 0) {
            const options = clients.map((c) => ({ label: c.label, id: c.id }));
            setClientOptions(options);
        }
    }, [clients]);

    /* ---- Handlers ---- */
    const handleClose = () => {
        setVisible(false);
        setName("");
        setSousService("");
        setLocalization("");
        setLunchBreakDuration(0);
        setFraisTransport(0);
        setForfaitDeplacement(0);
        setClient(null);
        setAttempt(false);
    };

    const handleAddTeam = () => {
        if (!client) {
            showErrorToast("Veuillez sélectionner un client");
            return;
        }
        if (name.trim() === "") {
            setAttempt(true);
            return;
        }
        dispatch(
            addTeam({
                id_client: client.id,
                name: name,
                sous_service: sousService,
                localization: localization,
                lunch_break_duration: lunchBreakDuration,
                frais_transport: fraisTransport,
                forfait_deplacement: forfaitDeplacement,
            })
        )
            .then((actionResponse) => {
                if (addTeam.fulfilled.match(actionResponse)) {
                    dispatch(getTeams());
                    handleClose();
                } else {
                    showErrorToast("Something went wrong");
                }
            })
            .catch((error) => {
                console.log(error);
                showErrorToast("Something went wrong");
            });
    };

    /* ---- Render ---- */
    return (
        <Dialog
            fullScreen
            open={!!visible}
            onClose={handleClose}
            TransitionComponent={Transition}
            sx={{
                zIndex: 1400,
                "& .MuiDialog-paper": {
                    background: "var(--surface-ground, #f4f6f8)",
                },
            }}
        >
            {/* Sticky AppBar */}
            <AppBar
                sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "rgba(255,255,255,0.82)",
                    color: "text.primary",
                    boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56 }}>
                    <IconButton
                        edge="start"
                        onClick={handleClose}
                        sx={{ mr: 1, color: "text.primary" }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        <Langs str="ADD_TEAM" />
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Scrollable content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    px: 2,
                    py: 2.5,
                    mt: 0,
                    pb: "100px",
                }}
            >
                <Box sx={glassCardSx}>
                    {/* Client */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="CLIENT" />
                    </Typography>
                    <Autocomplete
                        value={client}
                        onChange={(_, newVal) => setClient(newVal)}
                        options={clientOptions}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Client"
                                sx={glassmorphicTextFieldSx}
                                size="small"
                            />
                        )}
                        componentsProps={{
                            popper: { sx: { zIndex: 1500 } },
                        }}
                        sx={{ mb: 1 }}
                    />

                    {/* Team name */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="TEAM_NAME" />
                    </Typography>
                    <TextField
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={name.trim() === "" && attempt}
                        helperText={
                            name.trim() === "" && attempt
                                ? "Le nom de l'équipe est requis"
                                : ""
                        }
                        placeholder="Nom de l'équipe"
                        size="small"
                        sx={glassmorphicTextFieldSx}
                    />

                    {/* Sous Service */}
                    <Typography sx={fieldLabelSx}>Sous Service</Typography>
                    <TextField
                        fullWidth
                        value={sousService}
                        onChange={(e) => setSousService(e.target.value)}
                        placeholder="Sous Service"
                        size="small"
                        sx={glassmorphicTextFieldSx}
                    />

                    {/* Localization */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="localization" />
                    </Typography>
                    <TextField
                        fullWidth
                        select
                        value={localization}
                        onChange={(e) => setLocalization(e.target.value)}
                        placeholder="Localisation"
                        size="small"
                        sx={glassmorphicTextFieldSx}
                        SelectProps={{
                            MenuProps: { sx: { zIndex: 1500 } },
                        }}
                    >
                        <MenuItem value="">
                            <em>-- Sélectionner --</em>
                        </MenuItem>
                        {LOCATIONS.map((loc) => (
                            <MenuItem key={loc} value={loc}>
                                {loc}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box sx={glassCardSx}>
                    {/* Lunch break duration */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="LUNCH_BREAK_DURATION" />
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        value={lunchBreakDuration}
                        onChange={(e) =>
                            setLunchBreakDuration(
                                Math.min(24, Math.max(0, Number(e.target.value) || 0))
                            )
                        }
                        inputProps={{ min: 0, max: 24, inputMode: "numeric" }}
                        size="small"
                        sx={glassmorphicTextFieldSx}
                    />

                    {/* Forfait déplacement */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="FORFAIT_DEPLACEMENT" />
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        value={forfaitDeplacement}
                        onChange={(e) =>
                            setForfaitDeplacement(
                                Math.max(0, Number(e.target.value) || 0)
                            )
                        }
                        inputProps={{ min: 0, inputMode: "numeric" }}
                        size="small"
                        sx={glassmorphicTextFieldSx}
                    />

                    {/* Frais transport */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="FRAIS_TRANSPORT" />
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        value={fraisTransport}
                        onChange={(e) =>
                            setFraisTransport(
                                Math.max(0, Number(e.target.value) || 0)
                            )
                        }
                        inputProps={{ min: 0, inputMode: "numeric" }}
                        size="small"
                        sx={glassmorphicTextFieldSx}
                    />
                </Box>
            </Box>

            {/* Sticky bottom action bar */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    px: 2,
                    py: 2,
                    bgcolor: "rgba(255,255,255,0.88)",
                    backdropFilter: "blur(24px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                    borderTop: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                    zIndex: 10,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddTeam}
                    sx={{
                        borderRadius: "999px",
                        minHeight: 48,
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "1rem",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
                        "&:active": { transform: "scale(0.97)" },
                        boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                    }}
                >
                    <Langs str="ADD" />
                </Button>
            </Box>
        </Dialog>
    );
}
